import type { FuzzyDate, Lang } from './types';

/** Approximate sortable timestamp (ms) for ordering; unknown dates sort last. */
export function sortKey(d: FuzzyDate | undefined): number {
  if (!d) return Number.POSITIVE_INFINITY;
  const y = approxYear(d);
  if (y === null) return Number.POSITIVE_INFINITY;
  const start = d.start ? parsePartial(d.start) : null;
  if (start) return start.getTime();
  return Date.UTC(y, 6, 1);
}

/** Best single year for a fuzzy date (midpoint of ranges). */
export function approxYear(d: FuzzyDate | undefined, refYearForRelative?: number): number | null {
  if (!d) return null;
  switch (d.kind) {
    case 'exact':
    case 'month':
    case 'year':
    case 'about':
      return d.start ? yearOf(d.start) : null;
    case 'decade':
    case 'range': {
      const a = d.start ? yearOf(d.start) : null;
      const b = d.end ? yearOf(d.end) : a;
      if (a === null) return b;
      if (b === null) return a;
      return Math.round((a + b) / 2);
    }
    case 'relative':
      return d.start ? yearOf(d.start) : refYearForRelative ?? null;
    case 'before_after':
      return d.start ? yearOf(d.start) : null;
    case 'unknown':
    default:
      return null;
  }
}

export function isApproximate(d: FuzzyDate | undefined): boolean {
  if (!d) return true;
  if (d.kind === 'exact' && d.certainty !== 'uncertain' && d.certainty !== 'approximate') return false;
  return true;
}

export function yearOf(partial: string): number | null {
  const m = /^(-?\d{1,4})/.exec(partial);
  return m ? Number(m[1]) : null;
}

export function parsePartial(partial: string): Date | null {
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(partial);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = m[2] ? Number(m[2]) - 1 : 0;
  const da = m[3] ? Number(m[3]) : 1;
  return new Date(Date.UTC(y, mo, da));
}

export const monthNames: Record<Lang, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  he: ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'],
};

/**
 * Human label for a fuzzy date. Never renders an approximate date as if it
 * were exact (section 11.3).
 */
export function formatFuzzyDate(d: FuzzyDate | undefined, lang: Lang, opts: { short?: boolean } = {}): string {
  const he = lang === 'he';
  if (!d || d.kind === 'unknown') return he ? 'תאריך לא ידוע' : 'Date unknown';
  const about = he ? 'בערך ' : 'About ';
  switch (d.kind) {
    case 'exact': {
      const dt = d.start ? parsePartial(d.start) : null;
      if (!dt) return he ? 'תאריך לא ידוע' : 'Date unknown';
      const day = dt.getUTCDate();
      const mon = monthNames[lang][dt.getUTCMonth()];
      const y = dt.getUTCFullYear();
      const s = he ? `${day} ב${mon} ${y}` : opts.short ? `${mon.slice(0, 3)} ${day}, ${y}` : `${mon} ${day}, ${y}`;
      return d.certainty === 'approximate' || d.certainty === 'uncertain' ? about + s : s;
    }
    case 'month': {
      const dt = d.start ? parsePartial(d.start) : null;
      if (!dt) return he ? 'תאריך לא ידוע' : 'Date unknown';
      const s = `${monthNames[lang][dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
      return d.certainty === 'approximate' ? about + s : s;
    }
    case 'year': {
      const y = d.start ? yearOf(d.start) : null;
      return y === null ? (he ? 'תאריך לא ידוע' : 'Date unknown') : String(y);
    }
    case 'about': {
      const y = d.start ? yearOf(d.start) : null;
      return y === null ? (he ? 'תאריך לא ידוע' : 'Date unknown') : about + y;
    }
    case 'decade': {
      const y = d.start ? yearOf(d.start) : null;
      if (y === null) return he ? 'תאריך לא ידוע' : 'Date unknown';
      const dec = Math.floor(y / 10) * 10;
      return he ? `שנות ה-${dec}` : `${dec}s`;
    }
    case 'range': {
      const a = d.start ? yearOf(d.start) : null;
      const b = d.end ? yearOf(d.end) : null;
      if (a === null && b === null) return he ? 'תאריך לא ידוע' : 'Date unknown';
      return he ? `בין ${a ?? '?'} ל-${b ?? '?'}` : `Between ${a ?? '?'} and ${b ?? '?'}`;
    }
    case 'relative': {
      const y = d.start ? yearOf(d.start) : null;
      const base = d.displayText || (he ? 'תאריך יחסי' : 'Relative date');
      return y === null ? base : `${base} (${about}${y})`;
    }
    case 'before_after': {
      const y = d.start ? yearOf(d.start) : null;
      const word = d.beforeAfter === 'before' ? (he ? 'לפני' : 'Before') : he ? 'אחרי' : 'After';
      return d.displayText ? `${word} ${d.displayText}` : y === null ? word : `${word} ${y}`;
    }
    default:
      return he ? 'תאריך לא ידוע' : 'Date unknown';
  }
}

/** "1932" or "1925 – 2003" or "" for the bubble under a name. */
export function lifeYears(birth: FuzzyDate | undefined, death: FuzzyDate | undefined, deceased: boolean, lang: Lang): string {
  const b = approxYear(birth);
  const dd = approxYear(death);
  const bs = b === null ? '' : (isApproximate(birth) && birth?.kind !== 'year' ? '~' : '') + b;
  if (!deceased) return bs;
  const ds = dd === null ? '' : (isApproximate(death) && death?.kind !== 'year' ? '~' : '') + dd;
  if (!bs && !ds) return lang === 'he' ? 'ז״ל' : '';
  return `${bs} – ${ds}`;
}

export function ageInYear(birth: FuzzyDate | undefined, year: number): number | null {
  const b = approxYear(birth);
  return b === null ? null : year - b;
}

export function currentAge(birth: FuzzyDate | undefined, death: FuzzyDate | undefined, deceased: boolean): number | null {
  const b = approxYear(birth);
  if (b === null) return null;
  const end = deceased ? approxYear(death) : new Date().getFullYear();
  return end === null ? null : end - b;
}

/** Grouping label for the timeline: decade for approximate dates, year for exact/month. */
export function timelineGroup(d: FuzzyDate | undefined, lang: Lang): { key: string; label: string; approximate: boolean } {
  const y = approxYear(d);
  if (y === null) return { key: 'unknown', label: lang === 'he' ? 'תאריך לא ידוע' : 'Date unknown', approximate: true };
  if (d && (d.kind === 'exact' || d.kind === 'month' || d.kind === 'year')) {
    return { key: String(y), label: String(y), approximate: false };
  }
  const dec = Math.floor(y / 10) * 10;
  return { key: `d${dec}`, label: lang === 'he' ? `שנות ה-${dec}` : `${dec}s`, approximate: true };
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function fuzzyFromYear(year: number, kind: 'year' | 'about' = 'year'): FuzzyDate {
  return { kind, start: String(year), certainty: kind === 'about' ? 'approximate' : 'confirmed' };
}
